export interface ChunkRecoveryGateInput {
  isOnline: boolean
  probeOnline: boolean
  /** Reload attempts already made within the loop-detection window. */
  recentAttempts: number
  /** Hard cap on reloads within the window before we stop looping. */
  maxAttempts: number
}

export type ChunkRecoveryDecision =
  | { action: 'recover' }
  | { action: 'giveup' }
  | { action: 'skip'; reason: 'offline' | 'unreachable' }

/**
 * Decide what to do about a chunk-load failure (a failed dynamic `import()`).
 *
 * A chunk-load error has three possible causes with identical error messages:
 *   1. A new deploy removed the stale chunk hash the client was requesting.
 *      → A reload (which clears Cache Storage + fetches the new index) fixes it.
 *   2. The client is offline and the chunk was not precached.
 *      → Recovery (clearing every Cache Storage bucket) is catastrophic: it wipes
 *        the precached app shell and offline assets, leaving a white screen until
 *        connectivity returns. We must NOT reload while offline.
 *   3. A build artifact is genuinely missing/unservable (e.g. a chunk the deploy
 *      never published). Reloading can never fix this, so a naive
 *      reload-on-chunk-error loops forever on a blank page.
 *
 * So we require both `navigator.onLine` and a real server probe before clearing
 * caches (guards cause 2 — `navigator.onLine` lies behind captive portals and in
 * WKWebView), and we bound the number of reloads within a time window (guards
 * cause 3 — after `maxAttempts` reloads the caller surfaces a static retry screen
 * instead of looping).
 */
export function decideChunkRecoveryAction(input: ChunkRecoveryGateInput): ChunkRecoveryDecision {
  // Connectivity gating first — preserving the offline shell is the highest
  // priority invariant: never clear caches when we can't reach the server.
  if (!input.isOnline) return { action: 'skip', reason: 'offline' }
  if (!input.probeOnline) return { action: 'skip', reason: 'unreachable' }

  // Loop guard: the chunk is reachable-server-wise but still won't load even
  // after clean reloads. Stop and let the caller show a retry fallback.
  if (input.recentAttempts >= input.maxAttempts) return { action: 'giveup' }

  return { action: 'recover' }
}

/**
 * Keep only the reload-attempt timestamps that fall within `windowMs` of `now`.
 * Pure so the loop-termination guarantee is unit-testable; the windowed-aging
 * reset is deliberately the ONLY reset (no success/clear timer), because a timer
 * that fires mid-recovery would wipe the counter before the reload lands and
 * defeat the loop guard — the exact bug this replaces.
 */
export function pruneReloadAttempts(attempts: number[], now: number, windowMs: number): number[] {
  return attempts.filter(ts => typeof ts === 'number' && now - ts < windowMs)
}
